import React from 'react';
import { Linkedin, Twitter, Instagram } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useT from '../../i18n';
import { useLanguage } from '../../contexts/LanguageContext';
import Logo from '../../components/Logo';
import FooterBrandGlow from '../../components/FooterBrandGlow';
import { APP_VERSION } from '../../config/version';

/**
 * Editorial Scandinavian Footer — Facelift Fase 1 (Feb 2026).
 *
 * Light-theme redesign: bg-paper-cool (subtle warm-off-white), replacing
 * the previous dark charcoal-deep aesthetic. Aims for magazine "colophon"
 * feel: 5-col editorial grid (wordmark + 4 link columns), thin ink-lines
 * as dividers, understated social row.
 *
 * Kept from legacy:
 *  - Miljøfyrtårn certification badge (swapped to color-variant for light bg)
 *  - Copyright + HQ location
 *
 * Removed (intentional simplification per Fase 1 brief):
 *  - B2B letter-to-employers banner (moved into landing FinalCTA scope)
 *  - Footer stats-strip (was already hidden behind SHOW_FOOTER_STATS)
 *  - HQ e-mail contact block in banner (still linkable via Selskap → Kontakt)
 *  - Miljøfyrtårn placed subtly in bottom-bar, not primary content
 *  - Duplicated lang-toggle (only in navbar per brief)
 */
const Footer = () => {
  const t = useT();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const cols = t.footer.editorialCols || [];
  const mft = t.footer.miljofyrtarn;
  // Use color-variant on light-theme footer (was white-variant on dark theme)
  const mftLogoSrc = language === 'en'
    ? '/brand/miljofyrtarn-en-color.svg'
    : '/brand/miljofyrtarn-no-color.svg';

  const handleLinkClick = (e, link) => {
    // Route-based: use React Router. Anchor (#), mailto, and http(s) delegated to browser.
    const href = link.href;
    if (!href) return;
    if (href.startsWith('mailto:') || href.startsWith('http')) return; // native
    if (link.scroll) {
      e.preventDefault();
      const el = document.getElementById(link.scroll);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (window.location.pathname !== '/') {
        // Not on landing — navigate + hash
        navigate('/');
        setTimeout(() => {
          const el2 = document.getElementById(link.scroll);
          if (el2) el2.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
      return;
    }
    // Internal route
    if (href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
    }
  };

  return (
    <footer
      className="relative bg-paper-cool border-t border-ink/[0.08]"
      data-testid="footer"
    >
      <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-20">
        {/* Editorial 12-col grid: wordmark-block col-span-4 + 4x col-span-2 */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          {/* Brand logo + manifest */}
          <div className="md:col-span-4">
            <FooterBrandGlow>
              <Logo variant="wordmark-bronze" size={52} data-testid="footer-logo" />
            </FooterBrandGlow>
            <p className="font-sans text-sm leading-[1.7] text-ink/60 max-w-xs mt-4">
              {t.footer.manifest}
            </p>
          </div>

          {/* 4 link columns */}
          {cols.map((c, i) => (
            <div key={i} className="md:col-span-2" data-testid={`footer-col-${i}`}>
              <h4 className="font-sans text-xs uppercase tracking-widest font-medium text-ink/50 mb-4">
                {c.title}
              </h4>
              <ul className="space-y-2.5">
                {c.links.map((l, j) => (
                  <li key={j}>
                    <a
                      href={l.href}
                      onClick={(e) => handleLinkClick(e, l)}
                      className="font-sans text-sm text-ink/70 hover:text-ink transition-colors rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-cool"
                      data-testid={`footer-link-${i}-${j}`}
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom-bar: © / socials */}
        <div className="border-t border-ink/[0.08] mt-12 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="font-sans text-xs text-ink/50" data-testid="footer-copyright">
            {t.footer.bottomCopyright}
            {' · '}
            <span data-testid="footer-version">v{APP_VERSION}</span>
            {' · '}
            {t.footer.rightsReserved}
          </p>

          <div className="flex items-center gap-4">
            {/* Miljøfyrtårn — subtile plassering i bunn-bar */}
            <a
              href="https://www.miljofyrtarn.no/hva-er-miljofyrtarn/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={mft?.ariaLabel}
              data-testid="footer-miljofyrtarn-link"
              className="inline-flex items-center opacity-70 hover:opacity-100 transition-opacity rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-cool"
            >
              <img
                src={mftLogoSrc}
                alt={mft?.alt || 'Miljøfyrtårn'}
                width="80"
                height="30"
                loading="lazy"
                decoding="async"
                className="h-7 w-auto"
                data-testid="footer-miljofyrtarn-logo"
              />
            </a>

            {/* Social icons — stroke-only, subtile */}
            <div className="flex items-center gap-3" role="group" aria-label="Sosiale medier">
              <a
                href="https://linkedin.com/company/norvig"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-ink/50 hover:text-ink transition-colors rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-cool"
                data-testid="footer-social-linkedin"
              >
                <Linkedin size={16} strokeWidth={1.5} />
              </a>
              <a
                href="https://x.com/norvigtalents"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
                className="text-ink/50 hover:text-ink transition-colors rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-cool"
                data-testid="footer-social-x"
              >
                <Twitter size={16} strokeWidth={1.5} />
              </a>
              <a
                href="https://instagram.com/norvigtalents"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-ink/50 hover:text-ink transition-colors rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-cool"
                data-testid="footer-social-instagram"
              >
                <Instagram size={16} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
